// docs/111 TB1: the band in town. One card a hero of mine in the village: their face, level, XP and
// purse, a button for their stats card (`heroes/HeroSheet`) and one for their inventory (docs/71),
// where they wear, swap, sell, discard and trade as they would on the chart; a dot while a trade
// offer waits and the level-up call when one is ready. I and K open the band's first hero's
// inventory and card (docs/87); the inventory's tabs reach the rest.
import { useEffect } from 'react';
import { HeroSprite, Meter, OryxIcon } from '../../components';
import { useMyLevelUpOffers } from '../../../core/subscriptions/hooks';
import { CLASS_NAMES } from '../../../core/sim/adapt';
import { ariaKeys, isHotkey, useHotkeyOverrides, withKey } from '../../../core/input/hotkeys';
import { openHeroSheet } from '../../heroes/heroSheetStore';
import { openInventory } from '../../inventory/store';
import { useIncomingTrades } from '../../inventory/Trades';
import { useOverlay } from '../../overlay';
import { MAX_LEVEL, xpToReach } from '../World';

export interface BandHero { id: bigint; worldId: bigint; name: string; level: number; xp: number; gold: number; class: number; species: number; oryxFrame: number }

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function TownBand({ worldId, heroes }: { worldId: bigint; heroes: BandHero[] }) {
  const keys = useHotkeyOverrides();
  const levelUps = useMyLevelUpOffers();
  const incoming = useIncomingTrades(worldId);
  const first = heroes[0]?.id;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (first === undefined || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      if (isHotkey(e, 'town.inventory')) { e.preventDefault(); openInventory(first); }
      else if (isHotkey(e, 'town.heroSheet')) { e.preventDefault(); openHeroSheet(first); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [first]);
  if (!heroes.length) return null;
  return (
    <nav className="fk-town-band fk-hit" aria-label="Your band" data-testid="town-band">
      {heroes.map((h, i) => {
        const classId = CLASS_NAMES[h.class] ?? 'fighter';
        const next = h.level >= MAX_LEVEL ? undefined : xpToReach(h.level + 1);
        const floor = xpToReach(h.level);
        const offer = levelUps.find((o) => o.heroId === h.id);
        const offers = incoming.filter((t) => t.toHero === h.id).length;
        return (
          <div key={h.id.toString()} className={`fk-town-band__hero${offer ? ' fk-town-band__hero--ready' : ''}`} data-testid="town-band-hero" data-hero-id={h.id.toString()} data-hero-name={h.name}>
            <button type="button" className="fk-town-band__face" onClick={() => openHeroSheet(h.id)} data-testid="town-band-sheet"
              title={i === 0 ? withKey(`${h.name}'s stats card`, 'town.heroSheet', keys) : `${h.name}'s stats card`} {...(i === 0 ? { 'aria-keyshortcuts': ariaKeys('town.heroSheet', keys) } : {})}>
              <span className="fk-town-band__portrait"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={classId} scale={2} title={h.name} /></span>
              <span className="fk-town-band__text">
                <span className="fk-town-band__name">{h.name}<span className="fk-town-band__level">L{h.level}</span></span>
                <span className="fk-town-band__sub">{cap(classId)} · <OryxIcon name="item_coin_bag" scale={1} />{h.gold}</span>
              </span>
            </button>
            <button type="button" className="fk-town-band__bag" onClick={() => openInventory(h.id)} data-testid="town-band-inventory"
              title={i === 0 ? withKey(`${h.name}'s inventory`, 'town.inventory', keys) : `${h.name}'s inventory`} {...(i === 0 ? { 'aria-keyshortcuts': ariaKeys('town.inventory', keys) } : {})}>
              <OryxIcon name="item_147" scale={2} /><span className="fk-visually-hidden">{h.name}'s inventory</span>
              {offers > 0 && <span className="fk-town-band__dot" aria-label={`${offers} trade offer${offers === 1 ? '' : 's'}`} />}
            </button>
            <Meter kind="xp" size="sm" className="fk-town-band__xp" label="XP"
              value={next === undefined ? 1 : Math.max(0, h.xp - floor)} max={next === undefined ? 1 : Math.max(1, next - floor)}
              format={() => (next === undefined ? `${h.xp} xp · max level` : `${h.xp} / ${next} xp to L${h.level + 1}`)} />
            {offer && <button type="button" className="fk-town-band__levelup" onClick={() => useOverlay.getState().openLevelUp(offer.id)} data-testid="town-band-levelup">Level up!</button>}
          </div>
        );
      })}
    </nav>
  );
}
